(() => {
  'use strict';

  const observers = new WeakMap();

  const clamp = (value) => Math.min(255, Math.max(0, Number(value) || 0));

  const parseColor = (value) => {
    if (!value) return null;
    const raw = String(value).trim();
    if (!raw) return null;

    const simpleRgb = raw.match(/^(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})$/);
    if (simpleRgb) return simpleRgb.slice(1).map(clamp);

    const functionalRgb = raw.match(/^rgba?\(\s*(\d{1,3})\s*[, ]\s*(\d{1,3})\s*[, ]\s*(\d{1,3})/i);
    if (functionalRgb) return functionalRgb.slice(1).map(clamp);

    const hex = raw.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
    if (!hex) return null;

    let digits = hex[1];
    if (digits.length === 3) digits = digits.split('').map((digit) => `${digit}${digit}`).join('');
    return [0, 2, 4].map((index) => parseInt(digits.slice(index, index + 2), 16));
  };

  const readVariable = (element, properties) => {
    if (!element) return null;
    const names = Array.isArray(properties) ? properties : [properties];
    const styles = getComputedStyle(element);

    for (const name of names) {
      const value = element.style.getPropertyValue(name) || styles.getPropertyValue(name);
      const color = parseColor(value);
      if (color) return color;
    }

    return null;
  };

  const publishPalette = (root, config) => {
    if (!root?.isConnected) return;

    const primary = readVariable(root, config.primary);
    if (!primary) return;

    const secondary = readVariable(root, config.secondary) || primary;
    document.dispatchEvent(new CustomEvent('aura:setpalette', {
      detail: {
        primary,
        secondary,
        source: config.source
      }
    }));
  };

  const reactiveConfigs = [
    { selector: '[data-er-service-product]', primary: '--ers-primary', secondary: '--ers-secondary', source: 'service-product' },
    {
      selector: '.er-home-hero',
      primary: '--er-slide-primary',
      secondary: '--er-slide-secondary',
      source: 'home-hero'
    },
    {
      selector: '.erdisc',
      primary: '--er-accent',
      secondary: '--er-accent-2',
      source: 'music-discography'
    },
    {
      selector: '.ermusic[data-er-music-single], .ermusic[data-er-music-album], .ermusic[data-er-version^="cd-"]',
      primary: '--er-art-primary',
      secondary: '--er-art-secondary',
      source: 'music-product'
    },
    {
      selector: '.erapparel',
      primary: '--er-art-primary',
      secondary: '--er-art-secondary',
      source: 'apparel-product'
    },
    {
      selector: '.era',
      primary: ['--era-a', '--era-primary'],
      secondary: ['--era-b', '--era-secondary'],
      source: 'artist'
    },
    {
      selector: '.erb',
      primary: ['--erb-a', '--erb-primary'],
      secondary: ['--erb-b', '--erb-secondary'],
      source: 'author'
    },
    {
      selector: '[id^="ERAuraArticle-"]',
      primary: ['--er-primary', '--er-article-primary'],
      secondary: ['--er-secondary', '--er-article-secondary'],
      source: 'article'
    }
  ];

  const chooseOwner = () => {
    for (const config of reactiveConfigs) {
      const root = document.querySelector(config.selector);
      if (root) return { root, config };
    }
    return null;
  };

  const bindOwner = () => {
    const owner = chooseOwner();
    if (!owner) return;

    const { root, config } = owner;
    if (observers.has(root)) {
      publishPalette(root, config);
      return;
    }

    let frame = 0;
    const schedule = () => {
      if (frame) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        frame = 0;
        publishPalette(root, config);
      });
    };

    schedule();

    if ('MutationObserver' in window) {
      const observer = new MutationObserver(schedule);
      observer.observe(root, {
        attributes: true,
        attributeFilter: ['style', 'class'],
        subtree: config.source === 'home-hero'
      });
      observers.set(root, observer);
    }
  };

  const initialize = () => bindOwner();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', () => requestAnimationFrame(bindOwner));
  document.addEventListener('shopify:section:unload', () => requestAnimationFrame(bindOwner));
  document.addEventListener('shopify:section:reorder', () => requestAnimationFrame(bindOwner));
})();
